import { computed, effect, inject, Injectable, signal } from '@angular/core';
import {
  FacebookApiService,
  GetWhatsAppConnectionResponseInterface,
  WhatsAppConnectionStatusCode,
} from '@vendasta/facebook';
import { firstValueFrom } from 'rxjs';

export const Status = {
  NOT_CONNECTED: 'not_connected',
  CONNECTED: 'connected',
  TOKEN_INVALID: 'token_invalid',
  UNKNOWN: 'unknown',
  UNKNOWN_ERROR: 'unknown_error',
  EVENT_SUBSCRIPTION_MISSING: 'event_subscription_missing',
} as const;

type Status = (typeof Status)[keyof typeof Status];

@Injectable({
  providedIn: 'any',
})
export class WhatsAppConnectionService {
  status = computed(() => {
    if (this.loading()) {
      return Status.UNKNOWN;
    }
    const connection = this.connection();
    if (!connection) {
      return Status.NOT_CONNECTED;
    }
    if (!connection?.status) {
      return Status.UNKNOWN;
    }
    switch (connection.status.statusCode) {
      case WhatsAppConnectionStatusCode.WHATSAPP_STATUS_READY:
        return Status.CONNECTED;
      case WhatsAppConnectionStatusCode.WHATSAPP_STATUS_TOKEN_INVALID:
        return Status.TOKEN_INVALID;
      case WhatsAppConnectionStatusCode.WHATSAPP_STATUS_WEBHOOK_NOT_SETUP:
        return Status.EVENT_SUBSCRIPTION_MISSING;
      case WhatsAppConnectionStatusCode.WHATSAPP_STATUS_ERROR_GENERIC:
        return Status.UNKNOWN_ERROR;
      case WhatsAppConnectionStatusCode.WHATSAPP_STATUS_UNKNOWN:
        return Status.UNKNOWN;
      default:
        return Status.UNKNOWN;
    }
  });

  organizationID = signal<string | null>(null);
  connection = signal<GetWhatsAppConnectionResponseInterface | null>(null);
  loading = signal(false);

  facebookAPI = inject(FacebookApiService);

  constructor() {
    effect(async () => {
      await this.fetchAndStoreConnection(this.organizationID());
    });
  }

  async disconnect(): Promise<string> {
    const connectionID = this.connection()?.connection?.whatsappConnectionId;
    if (!connectionID) {
      return '';
    }
    try {
      await firstValueFrom(
        this.facebookAPI.deleteWhatsAppConnection({
          whatsappConnectionId: connectionID,
        }),
      );
      this.connection.set(null);
      return 'INBOX.SETTINGS.WHATSAPP_MESSAGES.SUCCESS.DISCONNECT_SUCCESSFUL';
    } catch (err) {
      throw new Error('INBOX.SETTINGS.WHATSAPP_MESSAGES.ERRORS.DISCONNECT_NOT_SUCCESSFUL');
    }
  }

  async connect(wabaID: string, phoneNumberID: string, code: string): Promise<string> {
    if (this.status() === Status.CONNECTED) {
      return '';
    }

    const organizationID = this.organizationID();
    if (!organizationID) {
      return '';
    }

    try {
      await firstValueFrom(
        this.facebookAPI.upsertWhatsAppConnection({
          whatsappBusinessAccountId: wabaID,
          organizationId: organizationID,
          phoneNumberId: phoneNumberID,
          code: code,
        }),
      );
      await this.fetchAndStoreConnection(this.organizationID());

      return 'INBOX.SETTINGS.WHATSAPP_MESSAGES.SUCCESS.SIGNUP_SUCCESSFUL';
    } catch (err) {
      throw new Error('INBOX.SETTINGS.WHATSAPP_MESSAGES.ERRORS.SIGNUP_NOT_SUCCESSFUL');
    }
  }

  private async fetchAndStoreConnection(organizationID: string | null) {
    if (!organizationID) {
      this.connection.set(null);
      return;
    }

    this.loading.set(true);
    try {
      const response = await firstValueFrom(
        this.facebookAPI.getWhatsAppConnection({ organizationId: organizationID, includeStatus: true }),
      );
      if (response) {
        this.connection.set(response);
      }
    } finally {
      this.loading.set(false);
    }
  }
}
